import { NextPage } from 'next';
import { useRouter } from 'next/router';

import { ProductShapeForm } from '@/components/custom/product-shape/form';
import { MLBLayout } from '@/components/custom/mlb-experience/form';
import { ErrorPage } from 'components/error.page';
import { LoadingPage } from 'components/loading.page';
import { useState } from 'react';
import { SliderLayout } from '@/components/layouts/slider';
import { useFormPublicQuery } from '@/graphql/query/form.public.query';

import ProductShape from '@/components/custom/product-shape/index';
import UXResearchShape from '@/components/custom/ux-research-shape';
import PillarsOfProductShape from '@/components/custom/pillars-of-product';
import DesignCapabilitiesShape from '@/components/custom/design-capabilities';
import ClientProductShape from '@/components/custom/client-product-shape';
import GenAiReadiness from '@/components/custom/gen-ai-readiness';

import ProductShapeJson from '@/json/product-shape-assessment/data.json';
import ClientProductShapeJson from '@/json/client-product-shape/data.json';
import UXResearchShapeJson from '@/json/ux-research-shape-assessment/data.json';
import PillarsOfProductJson from '@/json/pillars-of-product/data.json';
import CVSProductShapeJson from '@/json/cvs-product-shape/data.json';
import GenAiReadinessJson from '@/json/gen-ai-readiness/data.json';
import DesignCapabilitiesJson from '@/json/design-capabilities/data.json';
import DesignSkillStackJson from '@/json/design-skill-stack/data.json';
import { CustomStartPage } from '@/components/custom/gen-ai-readiness/start-page';
import DesignSkillStackLayout from '@/components/custom/design-skill-stack/layout';
import { InnovationLayout } from '@/components/custom/innovation-tool';
import { GEOLayout } from '@/components/custom/geo-impact';

const Index: NextPage = () => {
  const router = useRouter();
  const [uxResearchShapeSubId, setUxResearchShapeSubId] = useState<
    string | null
  >(null);
  let { loading, data, error } = useFormPublicQuery({
    variables: {
      id: router.query.id as string,
    },
    skip: !router.query.id,
  });

  if (loading || !router.query.id) {
    return <LoadingPage message={'Loading Form...'} />;
  }

  if (error) {
    return <ErrorPage error={error} />;
  }
  // Below fix for type error was found at https://stackoverflow.com/questions/55900443/object-is-possibly-undefined-in-reactjs-with-typescript
  // TODO: Possibly a better fix for this?
  switch (data?.form.id) {
    // Product Shape Assessment
    case 'd1dE7G':
      return (
        <ProductShapeForm
          form={data!.form}
          customFormJson={ProductShapeJson}
          renderCustomFormEndPage={(params) => (
            <ProductShape
              scores={params.scores}
              design={params.design}
              jsonData={params.jsonData}
            />
          )}
        />
      );
    // UX Research Shape Assessment
    case 'Q7x63V':
      const originalFields = data!.form.fields;
      const firstThree = originalFields.slice(0, 4);
      const shuffledFields = [...originalFields.slice(4)].sort(
        () => Math.random() - 0.5
      );
      const formWithshuffledFields = {
        ...data!.form,
        fields: [...firstThree, ...shuffledFields],
      };
      return (
        <ProductShapeForm
          form={formWithshuffledFields}
          customFormJson={UXResearchShapeJson}
          setSubmissionId={setUxResearchShapeSubId}
          renderCustomFormEndPage={(params) => {
            return (
              <UXResearchShape
                scores={params.scores}
                design={params.design}
                jsonData={params.jsonData}
                submissionId={uxResearchShapeSubId}
              />
            );
          }}
        />
      );
    // Pillars of Product Capability Assessment
    case 'Q7wO27':
      return (
        <ProductShapeForm
          form={data!.form}
          customFormJson={PillarsOfProductJson}
          renderCustomFormEndPage={(params) => (
            <PillarsOfProductShape
              scores={params.scores}
              design={params.design}
              jsonData={params.jsonData}
              questions={params.questions}
              userAnswers={params.userAnswers}
            />
          )}
        />
      );
    // CVS Product Shape Survey
    case 'Y1n691':
      return (
        <ProductShapeForm
          form={data!.form}
          customFormJson={CVSProductShapeJson}
        />
      );
    // Client Product Shape Assessment
    case 'wjXenV':
      return (
        <ProductShapeForm
          form={data!.form}
          customFormJson={ClientProductShapeJson}
          renderCustomFormEndPage={(params) => (
            <ClientProductShape
              scores={params.scores}
              design={params.design}
              jsonData={params.jsonData}
              questions={params.questions}
              userAnswers={params.userAnswers}
            />
          )}
        />
      );
    // Design Capabilities Assessment
    case 'yj5OE1':
      return (
        <ProductShapeForm
          form={data!.form}
          customFormJson={DesignCapabilitiesJson}
          renderCustomFormEndPage={(params) => (
            <DesignCapabilitiesShape
              scores={params.scores}
              design={params.design}
              jsonData={params.jsonData}
              questions={params.questions}
              userAnswers={params.userAnswers}
            />
          )}
        />
      );
    // MLB Fan Experience
    case 'gjZl61':
      return <MLBLayout form={data!.form} />;
    // GenAiReadiness Calc
    case 'ejKQZ1':
      const startPageFieldIds = GenAiReadinessJson.questions
        .filter((item) => {
          if (item.setOnCustomPage) {
            return item;
          }
        })
        .map((item) => item.questionId);
      const startPageFields = data!.form.fields.filter(
        (item) => startPageFieldIds.indexOf(item.id) > -1
      );
      const updatedFields = data!.form.fields.map((item) => {
        if (startPageFieldIds.indexOf(item.id) > -1) {
          return { ...item, ...{ type: 'hidden' } };
        }
        return item;
      });
      const formData = {
        ...data,
        ...{ form: { ...data.form, fields: updatedFields } },
      };
      return (
        <SliderLayout
          form={formData!.form}
          customFormJson={GenAiReadinessJson}
          startPageFields={startPageFields}
          renderCustomFormEndPage={(params) => (
            <GenAiReadiness
              scores={params.scores}
              design={params.design}
              jsonData={params.jsonData}
              questions={params.questions}
              userAnswers={params.userAnswers}
            />
          )}
          renderCustomFormStartPage={(params) => (
            <CustomStartPage
              startPageFields={startPageFields}
              design={params.design}
            />
          )}
        />
      );
    // Design Skill Stack
    case '5jplEj':
      return (
        <DesignSkillStackLayout
          form={data!.form}
          customFormJson={DesignSkillStackJson}
        />
      );
    // DP&I Innovation Tool
    case 'Q7xyqj':
      return <InnovationLayout form={data!.form} />;

    // GEO impact
    case 'qV8DQV':
      return <GEOLayout form={data!.form} />;
    // All other forms
    default:
      return <SliderLayout form={data!.form} />;
  }
};

export default Index;
